import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { useDispatch } from "react-redux";

import CommonButton from "../Utils/CommonButton";
import { LabelText } from "../Utils/Input";
import InputField from "../Utils/InputField";

import { createCategory } from "../../slices/Category/AddCategory/thunk";
import { editCategory } from "../../slices/Category/EditCategory/thunk";
import { editSubCategory } from "../../slices/Category/EditSubCategory/thunk";
import { createSubCategory } from "../../slices/Category/AddSubCategory/thunk";
import { Loader } from "../Utils/Loader";
import { toast } from "react-toastify";

function CategoryForm({
  closeModal,
  onSuccess,
  selectedCategory,
  navigationStack,
}) {
  const dispatch = useDispatch();
  const isEditMode = !!selectedCategory;

  const [values, setValues] = useState({
    categoryName: "",
    description: "",
  });

  const [showPreview, setShowPreview] = useState(false);
  const [subCategories, setSubCategories] = useState([
    {
      name: "",
      expanded: true,
      types: [],
    },
  ]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isAddingSubCategory = !selectedCategory && navigationStack?.length > 0;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addSubCategory = () => {
    setSubCategories([
      ...subCategories,
      {
        name: "",
        expanded: true,
        types: [],
      },
    ]);
  };

  const removeSubCategory = (subIndex) => {
    const updated = subCategories.filter((_, i) => i !== subIndex);

    setSubCategories(updated);
  };

  const handleSubCategoryChange = (value, subIndex) => {
    const updated = [...subCategories];

    updated[subIndex].name = value;

    setSubCategories(updated);
  };

  const toggleSubCategory = (subIndex) => {
    const updated = [...subCategories];

    updated[subIndex].expanded = !updated[subIndex].expanded;

    setSubCategories(updated);
  };

  // TYPE

  const addType = (subIndex) => {
    const updated = [...subCategories];

    updated[subIndex].types.push({
      name: "",
      expanded: true,
      variants: [],
    });

    setSubCategories(updated);
  };

  const removeType = (subIndex, typeIndex) => {
    const updated = [...subCategories];

    updated[subIndex].types = updated[subIndex].types.filter(
      (_, i) => i !== typeIndex,
    );

    setSubCategories(updated);
  };

  const handleTypeChange = (value, subIndex, typeIndex) => {
    const updated = [...subCategories];

    updated[subIndex].types[typeIndex].name = value;

    setSubCategories(updated);
  };

  const toggleType = (subIndex, typeIndex) => {
    const updated = [...subCategories];

    updated[subIndex].types[typeIndex].expanded =
      !updated[subIndex].types[typeIndex].expanded;

    setSubCategories(updated);
  };

  // VARIANT

  const addVariant = (subIndex, typeIndex) => {
    const updated = [...subCategories];

    updated[subIndex].types[typeIndex].variants.push("");

    setSubCategories(updated);
  };

  const removeVariant = (subIndex, typeIndex, variantIndex) => {
    const updated = [...subCategories];

    updated[subIndex].types[typeIndex].variants = updated[subIndex].types[
      typeIndex
    ].variants.filter((_, i) => i !== variantIndex);

    setSubCategories(updated);
  };

  const handleVariantChange = (value, subIndex, typeIndex, variantIndex) => {
    const updated = [...subCategories];

    updated[subIndex].types[typeIndex].variants[variantIndex] = value;

    setSubCategories(updated);
  };

  // VALIDATION

  const validate = () => {
    let newErrors = {};

    if (!values.categoryName.trim()) {
      newErrors.categoryName = "Category Name is required";
    }

    return newErrors;
  };

  const parseSubcategory = (subcategoryObj = {}) => {
    return Object.values(subcategoryObj).map((sub) => ({
      name: sub.name || "",
      expanded: true,
      types: sub.subcategory
        ? Object.values(sub.subcategory).map((type) => ({
            name: type.name || "",
            expanded: true,
            variants: type.subcategory
              ? Object.values(type.subcategory).map((v) => v.name)
              : [],
          }))
        : [],
    }));
  };

  useEffect(() => {
    if (!selectedCategory) return;

    setValues({
      categoryName: selectedCategory.name || "",
      description: selectedCategory.description || "",
    });

    setSubCategories(parseSubcategory(selectedCategory.subcategory || {}));
  }, [selectedCategory]);

  // SUBMIT

  const buildSubcategory = (items) => {
    const result = {};

    items.forEach((sub) => {
      const subObj = {
        name: sub.name,
        description: `all ${sub.name}`,
      };

      if (sub.types?.length > 0) {
        subObj.subcategory = {};

        sub.types.forEach((type) => {
          const typeObj = {
            name: type.name,
            description: `all ${type.name}`,
          };

          if (type.variants?.length > 0) {
            typeObj.subcategory = {};

            type.variants.forEach((variant) => {
              typeObj.subcategory[variant] = {
                name: variant,
                description: `all ${variant}`,
              };
            });
          }

          subObj.subcategory[type.name] = typeObj;
        });
      }

      result[sub.name] = subObj;
    });

    return result;
  };

  const handleSubmit = () => {
    const validationErrors = validate();

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fill mandatory fields");
      return;
    }

    setLoading(true);
    if (isAddingSubCategory) {
      const payload = {
        [values.categoryName]: {
          name: values.categoryName,
          description:
            values.description ||
            `${values.categoryName} ${values.categoryName}`,
        },
      };

      // ADD NESTED SUBCATEGORY ONLY IF EXISTS
      if (subCategories?.length > 0) {
        const nestedSubcategory = {};

        subCategories.forEach((sub) => {
          if (!sub.name?.trim()) return;

          nestedSubcategory[sub.name] = {
            name: sub.name,
            description: sub.description || `${sub.name} ${sub.name}`,
          };

          // TYPES
          if (sub.types?.length > 0) {
            const typeObj = {};

            sub.types.forEach((type) => {
              if (!type.name?.trim()) return;

              typeObj[type.name] = {
                name: type.name,
                description: type.description || `${type.name} ${type.name}`,
              };

              // VARIANTS
              if (type.variants?.length > 0) {
                const variantObj = {};

                type.variants.forEach((variant) => {
                  if (!variant?.trim()) return;

                  variantObj[variant] = {
                    name: variant,
                    description: `${variant} ${variant}`,
                  };
                });

                // ADD VARIANT ONLY IF EXISTS
                if (Object.keys(variantObj).length > 0) {
                  typeObj[type.name].subcategory = variantObj;
                }
              }
            });

            // ADD TYPES ONLY IF EXISTS
            if (Object.keys(typeObj).length > 0) {
              nestedSubcategory[sub.name].subcategory = typeObj;
            }
          }
        });

        // ADD SUBCATEGORY ONLY IF EXISTS
        if (Object.keys(nestedSubcategory).length > 0) {
          payload[values.categoryName].subcategory = nestedSubcategory;
        }
      }

      const pathArray = navigationStack.map((item) => item.name);

      dispatch(
        createSubCategory(payload, pathArray, setLoading, () => {
          setLoading(false);
          onSuccess?.();
          closeModal?.();
        }),
      );

      return;
    }

    const subcategoryData = buildSubcategory(
      subCategories.filter((sub) => sub.name?.trim()),
    );

    const payload = {
      name: values.categoryName,
      description: values.description,
    };

    if (Object.keys(subcategoryData).length > 0) {
      payload.subcategory = subcategoryData;
    }

    // EDIT CATEGORY
    if (selectedCategory?.categoryid) {
      if (selectedCategory?.isSubCategory === true) {
        dispatch(
          editSubCategory(
            {
              name: values.categoryName,
              description: values.description,
              itemcount: selectedCategory?.itemcount || 0,
            },
            selectedCategory?.rootCategoryId,
            selectedCategory?.pathArray || [],
            setLoading,
            () => {
              setLoading(false);
              onSuccess?.();
              closeModal?.();
            },
          ),
        );
      } else {
        dispatch(
          editCategory(payload, selectedCategory.categoryid, setLoading, () => {
            setLoading(false);
            onSuccess?.();
            closeModal?.();
          }),
        );
      }
    }

    // CREATE CATEGORY
    else {
      dispatch(
        createCategory(payload, setLoading, () => {
          setLoading(false);
          onSuccess?.();
          closeModal?.();
        }),
      );
    }
  };

  return (
    <>
      {loading && <Loader />}

      <Row>
        <Col md={12}>
          <LabelText
            htmlFor={"categoryName"}
            className={"text-danger"}
            displayText={"Category Name"}
            important={"*"}
          />

          <InputField
            name="categoryName"
            type="text"
            id="categoryName"
            className="form-control"
            handleChange={handleChange}
            value={values.categoryName}
            invalid={errors.categoryName ? true : false}
            placeholder="Enter category name"
          />
        </Col>

        {!isEditMode && (
          <Col md={12} className="mt-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
              <div>
                <h4 className="fw-bold mb-1">Sub-Categories</h4>

                <p className="text-muted mb-0">
                  Add sub-categories, types and variants
                </p>
              </div>

              <Button
                className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1"
                onClick={addSubCategory}
              >
                <i className="ri-add-fill label-icon align-middle fs-16 me-1 fw-bold"></i>
                <span className="ps-2 ms-2 me-1 fw-bold">Add Sub-Category</span>
              </Button>
            </div>

            {showPreview ? (
              <div className="border rounded-4 p-3 bg-white">
                {subCategories.map((sub, subIndex) => (
                  <div key={subIndex}>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                      {/* LEFT */}

                      <div className="flex-grow-1">
                        <div className="border rounded-3 px-4 py-2 d-inline-block mb-3 bg-light fw-medium fs-6">
                          {sub.name}
                        </div>

                        {sub.types.map((type, typeIndex) => (
                          <div key={typeIndex}>
                            {type.variants.map((variant, variantIndex) => (
                              <div
                                key={variantIndex}
                                className="fw-medium fs-6 mb-2"
                              >
                                {sub.name} {" >> "} {type.name} {" >> "}{" "}
                                {variant}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <i
                          className="ri-edit-box-line fs-4 text-dark cursor-pointer"
                          role="button"
                          onClick={() => setShowPreview(false)}
                        ></i>

                        <i
                          className="ri-delete-bin-line fs-4 text-danger cursor-pointer"
                          role="button"
                          onClick={() => {
                            setSubCategories([
                              {
                                name: "",
                                expanded: true,
                                types: [],
                              },
                            ]);
                            setShowPreview(false);
                          }}
                        ></i>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="border p-3 bg-white overflow-auto"
                style={{
                  maxHeight: "500px",
                  borderRadius: "7px",
                }}
              >
                {subCategories.map((sub, subIndex) => (
                  <div
                    key={subIndex}
                    className="border p-3 mb-4 bg-white"
                    style={{ borderRadius: "7px" }}
                  >
                    <div className="d-flex flex-wrap align-items-center gap-3">
                      <i
                        className={`ri-arrow-${
                          sub.expanded ? "down" : "right"
                        }-s-line fs-3`}
                        role="button"
                        onClick={() => toggleSubCategory(subIndex)}
                      ></i>

                      <div
                        className="border rounded-1 d-flex align-items-center justify-content-center bg-light"
                        style={{
                          width: "45px",
                          height: "45px",
                        }}
                      >
                        {subIndex + 1}
                      </div>

                      <div className="flex-grow-1">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter sub-category"
                          value={sub.name}
                          onChange={(e) =>
                            handleSubCategoryChange(e.target.value, subIndex)
                          }
                        />
                      </div>

                      <i
                        className="ri-delete-bin-line fs-3 text-danger"
                        role="button"
                        onClick={() => removeSubCategory(subIndex)}
                      ></i>
                    </div>

                    {/* TYPES */}

                    {sub.expanded && (
                      <div className="mt-4 ps-md-5">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                          <h6 className="mb-0 fw-bold">Types</h6>

                          <Button
                            className="rounded-1 px-4 py-1 fw-bold border-0 d-flex align-items-center fw-normal"
                            style={{
                              backgroundColor: "#FD75451A",
                              color: "#FD7545",
                            }}
                            onClick={() => addType(subIndex)}
                          >
                            <i className="ri-add-line me-1"></i>
                            Add Type
                          </Button>
                        </div>

                        {sub.types.length === 0 && (
                          <div
                            className="border rounded-3 text-center py-1 text-muted bg-light"
                            style={{
                              fontSize: "12px",
                            }}
                          >
                            No types added
                          </div>
                        )}

                        {sub.types.map((type, typeIndex) => (
                          <div
                            key={typeIndex}
                            className="border p-3 mb-3 bg-light"
                            style={{ borderRadius: "7px" }}
                          >
                            <div className="d-flex flex-wrap align-items-center gap-3">
                              <i
                                className={`ri-arrow-${
                                  type.expanded ? "down" : "right"
                                }-s-line fs-3`}
                                role="button"
                                onClick={() => toggleType(subIndex, typeIndex)}
                              ></i>
                              <div
                                className="border rounded-1 d-flex align-items-center justify-content-center bg-white fw-semibold"
                                style={{
                                  width: "58px",
                                  height: "38px",
                                  minWidth: "58px",
                                  fontSize: "13px",
                                }}
                              >
                                {subIndex + 1}.{typeIndex + 1}
                              </div>

                              <div className="flex-grow-1">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter Type"
                                  value={type.name}
                                  onChange={(e) =>
                                    handleTypeChange(
                                      e.target.value,
                                      subIndex,
                                      typeIndex,
                                    )
                                  }
                                />
                              </div>

                              <i
                                className="ri-delete-bin-line fs-3 text-danger"
                                role="button"
                                onClick={() => removeType(subIndex, typeIndex)}
                              ></i>
                            </div>
                            {type.expanded && (
                              <div className="mt-4 ps-md-5">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                                  <h6 className="mb-0 fw-bold">Variants</h6>

                                  <Button
                                    className="rounded-1 px-4 py-1 fw-bold border-0 d-flex align-items-center fw-normal"
                                    style={{
                                      backgroundColor: "#FD75451A",
                                      color: "#FD7545",
                                    }}
                                    onClick={() =>
                                      addVariant(subIndex, typeIndex)
                                    }
                                  >
                                    <i className="ri-add-line me-1"></i>
                                    Add Variant
                                  </Button>
                                </div>
                                {type.variants.length === 0 && (
                                  <div
                                    className="border rounded-3 text-center py-1 text-muted bg-white"
                                    style={{
                                      fontSize: "12px",
                                    }}
                                  >
                                    No variants added
                                  </div>
                                )}

                                {type.variants.map((variant, variantIndex) => (
                                  <div
                                    key={variantIndex}
                                    className="d-flex flex-wrap align-items-center gap-3 mb-3"
                                  >
                                    {/* VARIANT INDEX */}

                                    <div
                                      className="border rounded-1 d-flex align-items-center justify-content-center bg-white fw-semibold"
                                      style={{
                                        width: "75px",
                                        height: "38px",
                                        minWidth: "75px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {subIndex + 1}.{typeIndex + 1}.
                                      {variantIndex + 1}
                                    </div>

                                    <div className="flex-grow-1">
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter variant"
                                        value={variant}
                                        onChange={(e) =>
                                          handleVariantChange(
                                            e.target.value,
                                            subIndex,
                                            typeIndex,
                                            variantIndex,
                                          )
                                        }
                                      />
                                    </div>

                                    <i
                                      className="ri-delete-bin-line fs-3 text-danger"
                                      role="button"
                                      onClick={() =>
                                        removeVariant(
                                          subIndex,
                                          typeIndex,
                                          variantIndex,
                                        )
                                      }
                                    ></i>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button
                    color="light"
                    className="border px-3 py-0"
                    style={{
                      fontSize: "13px",
                      minWidth: "80px",
                      height: "30px",
                      lineHeight: "1",
                    }}
                    onClick={() => {
                      setSubCategories([
                        {
                          name: "",
                          expanded: true,
                          types: [],
                        },
                      ]);
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    className="rv-primary-btn px-3 py-0"
                    style={{
                      fontSize: "13px",
                      minWidth: "70px",
                      height: "30px",
                      lineHeight: "1",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={() => setShowPreview(true)}
                  >
                    <i className="mdi mdi-check me-1"></i>
                    OK
                  </Button>
                </div>
              </div>
            )}
          </Col>
        )}

        {/* DESCRIPTION */}

        <Col md={12} className="mt-3">
          <LabelText displayText={"Description"} />

          <InputField
            name="description"
            type="textarea"
            className="form-control"
            handleChange={handleChange}
            value={values.description}
            placeholder={"Enter description"}
          />
        </Col>

        <div
          className="d-flex justify-content-center w-100 gap-2 flex-wrap pe-1 py-3"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #e9ebec",
            marginTop: "20px",
          }}
        >
          <div className="d-flex gap-2 justify-content-center">
            <CommonButton
              outline={true}
              className={
                "btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
              }
              color={"main-color"}
              type={"reset"}
              buttonAction={() => {
                closeModal();
              }}
              buttonText={"Cancel"}
              icon={
                <i className="ri-close-fill label-icon secondary-label-icon align-middle fs-16 me-2"></i>
              }
              btnTxtClsName={"px-2 ps-3 ms-2 me-1 fw-bold"}
            />
            <CommonButton
              color={"main-color"}
              className={
                "btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
              }
              icon={
                <i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-2"></i>
              }
              btnTxtClsName={"px-2 ps-3 ms-2 me-1 fw-bold"}
              type={"submit"}
              buttonText={"Submit"}
              buttonAction={handleSubmit}
            />
          </div>
        </div>
      </Row>
    </>
  );
}

export default CategoryForm;
